import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PropertyCard, PropertyCardSkeleton } from '../components/listings/PropertyCard';
import { FilterPanel } from '../components/listings/FilterPanel';
import { SaveAsLeadDialog } from '../components/dashboard/SaveAsLeadDialog';
import { Button } from '../components/ui/Button';
import { useListingsStore } from '../stores/listings';
import { useAuthStore } from '../stores/auth';
import { Map, Home as HomeIcon, RefreshCw, ArrowUp, SearchX } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Listing } from '../services/api';

export default function Listings() {
  const [searchParams] = useSearchParams();
  const {
    listings,
    pagination,
    isLoading,
    error,
    filters,
    setFilters,
    fetchListings,
    resetFilters,
    toggleFavorite,
  } = useListingsStore();

  const { isAuthenticated } = useAuthStore();
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [leadListing, setLeadListing] = useState<Listing | null>(null);

  // Highlighted listing IDs from push notification
  const highlightedIds = useMemo(() => {
    const raw = searchParams.get('highlight');
    if (!raw) return new Set<string>();
    return new Set(raw.split(','));
  }, [searchParams]);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const initialLoadDone = useRef(false);

  // Track previous filters for comparison (excluding page)
  const prevFiltersRef = useRef<string>('');

  // Initialize filters from URL params on mount
  useEffect(() => {
    const city = searchParams.get('city');
    const contractType = searchParams.get('contractType');
    const propertyType = searchParams.get('propertyType');

    const newFilters: Record<string, string | undefined> = {};
    if (city) newFilters.city = city;
    if (contractType) newFilters.contractType = contractType;
    if (propertyType) newFilters.propertyType = propertyType;

    if (Object.keys(newFilters).length > 0) {
      setFilters(newFilters);
    }
  }, [searchParams, setFilters]);

  // Create a stable filter key for comparison (excluding page and limit)
  const filterKey = useMemo(() => {
    const { page, limit, ...restFilters } = filters;
    return JSON.stringify(restFilters);
  }, [filters]);

  // Fetch listings when filters change (but not on page change for infinite scroll)
  useEffect(() => {
    // Skip if filters haven't actually changed
    if (prevFiltersRef.current === filterKey && initialLoadDone.current) {
      return;
    }

    prevFiltersRef.current = filterKey;
    initialLoadDone.current = true;

    // Reset to page 1 when filters change
    if (filters.page !== 1) {
      setFilters({ page: 1 }, false);
    }

    fetchListings();
  }, [filterKey, fetchListings, filters.page, setFilters]);

  // Track scroll position for "back to top" button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Debounced load more function to prevent multiple triggers
  const handleLoadMore = useCallback(() => {
    if (isLoadingMoreRef.current || isLoading || !pagination?.hasMore) {
      return;
    }

    isLoadingMoreRef.current = true;
    const nextPage = (filters.page || 1) + 1;

    // Use setTimeout to debounce rapid scroll events
    setTimeout(() => {
      setFilters({ page: nextPage }, false);
      fetchListings(true).finally(() => {
        isLoadingMoreRef.current = false;
      });
    }, 100);
  }, [isLoading, pagination, filters.page, setFilters, fetchListings]);

  // Setup intersection observer for infinite scroll with debounce
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMoreRef.current) {
          handleLoadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px', // Start loading before reaching the end
      }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleLoadMore]);

  const handleSaveAsLead = useCallback((listing: Listing) => {
    setLeadListing(listing);
    setLeadDialogOpen(true);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle reset filters with fetch
  const handleResetFilters = useCallback(async () => {
    resetFilters();
    await fetchListings();
  }, [resetFilters, fetchListings]);

  // Build filter summary for feedback
  const getFilterSummary = () => {
    const parts: string[] = [];
    if (filters.city && filters.city !== 'Milano') parts.push(filters.city);
    else if (filters.city === 'Milano') parts.push('Milano');
    if (filters.contractType === 'SALE') parts.push('Vendita');
    if (filters.contractType === 'RENT') parts.push('Affitto');
    if (filters.minPrice || filters.maxPrice) {
      const min = filters.minPrice ? `${(filters.minPrice / 1000).toFixed(0)}k` : '';
      const max = filters.maxPrice ? `${(filters.maxPrice / 1000).toFixed(0)}k` : '';
      if (min && max) parts.push(`${min} - ${max}`);
      else if (min) parts.push(`da ${min}`);
      else if (max) parts.push(`fino a ${max}`);
    }
    if (filters.minRooms) {
      parts.push(`${filters.minRooms}+ locali`);
    }
    return parts.length > 0 ? parts.join(' - ') : null;
  };

  const filterSummary = getFilterSummary();

  // Count active filters for display
  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (['page', 'limit', 'sortBy', 'sortOrder', 'liveSortBy'].includes(key)) return false;
    if (value === undefined || value === null || value === '') return false;
    if (key === 'city' && value === 'Milano') return false;
    return true;
  }).length;

  return (
    <div ref={scrollContainerRef} className="min-h-screen bg-gray-50">
      {/* Mobile: Sticky filter bar */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm md:hidden">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <FilterPanel className="flex-1" />
            <Link to="/map">
              <Button variant="outline" size="sm" className="h-12 px-4">
                <Map className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Desktop: Horizontal filter bar */}
      <div className="hidden md:block sticky top-16 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <FilterPanel className="flex-1" />
            <div className="flex items-center gap-2">
              <Link to="/map">
                <Button variant="outline" size="sm">
                  <Map className="mr-2 h-4 w-4" />
                  Mappa
                </Button>
              </Link>
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <HomeIcon className="mr-2 h-4 w-4" />
                  Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 md:py-6">
        {/* Results header with count and filter summary */}
        <div className="mb-4 md:mb-6">
          {isLoading && listings.length === 0 ? (
            <div className="flex items-center gap-2">
              <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
            </div>
          ) : pagination ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  {pagination.total !== null
                    ? `${pagination.total.toLocaleString('it-IT')} annunci`
                    : `${listings.length} annunci caricati`}
                </h1>
                {filterSummary && (
                  <p className="text-sm text-gray-500 mt-0.5">{filterSummary}</p>
                )}
              </div>

              {/* Loading indicator while fetching more */}
              {isLoading && listings.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Caricamento...</span>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Error state */}
        {error && (
          <div className="mb-6 rounded-xl bg-red-50 border border-red-100 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-red-800 font-medium">Errore nel caricamento</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchListings()}
                className="flex-shrink-0"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Riprova
              </Button>
            </div>
          </div>
        )}

        {/* Initial loading - Skeleton grid */}
        {isLoading && listings.length === 0 && (
          <div className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <PropertyCardSkeleton key={`skeleton-${i}`} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && listings.length === 0 && !error && (
          <div className="text-center py-16 px-4">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
              <SearchX className="h-10 w-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Nessun annuncio trovato
            </h2>
            <p className="text-gray-500 mb-4 max-w-sm mx-auto">
              {activeFiltersCount > 0
                ? 'Prova a modificare i filtri di ricerca o rimuovi alcuni criteri'
                : 'Non ci sono annunci disponibili per questa citta'}
            </p>
            {activeFiltersCount > 0 && (
              <p className="text-sm text-gray-400 mb-6">
                {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro attivo' : 'filtri attivi'}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {activeFiltersCount > 0 && (
                <Button
                  variant="outline"
                  onClick={handleResetFilters}
                >
                  Resetta filtri
                </Button>
              )}
              <Button asChild>
                <Link to="/">Torna alla home</Link>
              </Button>
            </div>
          </div>
        )}

        {/* Listings grid */}
        {listings.length > 0 && (
          <div className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {listings.map((listing, index) => (
              <PropertyCard
                key={listing.id || `listing-${index}`}
                listing={listing}
                onFavoriteToggle={toggleFavorite}
                onSaveAsLead={isAuthenticated ? handleSaveAsLead : undefined}
                isHighlighted={highlightedIds.has(listing.id)}
              />
            ))}

            {/* Loading skeletons for infinite scroll */}
            {isLoading &&
              Array.from({ length: 4 }).map((_, i) => (
                <PropertyCardSkeleton key={`skeleton-loading-${i}`} />
              ))}
          </div>
        )}

        {/* Load more trigger */}
        {pagination?.hasMore && (
          <div ref={loadMoreRef} className="h-24 flex items-center justify-center">
            {isLoading && (
              <div className="flex items-center gap-2 text-gray-500">
                <RefreshCw className="h-5 w-5 animate-spin" />
                <span>Caricamento altri annunci...</span>
              </div>
            )}
          </div>
        )}

        {/* End of results */}
        {pagination && !pagination.hasMore && listings.length > 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">Hai visto tutti gli annunci</p>
            <Button variant="outline" onClick={scrollToTop}>
              <ArrowUp className="mr-2 h-4 w-4" />
              Torna su
            </Button>
          </div>
        )}
      </div>

      {/* Floating "back to top" button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 md:bottom-8 right-4 z-40 h-12 w-12 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 active:scale-95 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          aria-label="Torna all'inizio"
        >
          <ArrowUp className="h-5 w-5 text-gray-700" />
        </button>
      )}

      {/* Spacer for mobile bottom nav */}
      <div className="h-20 md:h-0" />

      {/* Save as Lead dialog */}
      <SaveAsLeadDialog
        open={leadDialogOpen}
        onOpenChange={setLeadDialogOpen}
        listing={leadListing}
        onLeadCreated={() => setLeadDialogOpen(false)}
      />
    </div>
  );
}
